/**
 * What the routes, the actions and the tabs show of a configuration, derived
 * from the descriptor — the bounds of the « Configuration » tabs (computed
 * by the tab itself since 2026-10-21, still sent by the route), the
 * promotion dialog's summary, the files volumes `waadoo:env:configure`
 * notes — and the CloudNativePG figures it waits for. Generic over the type
 * since step 6 of the genericity plan (2026-10-24): no field id, no Odoo
 * shape.
 */

import {
  MODE_INSTANCES,
  isDatabaseMode,
  type AppTypeDescriptor,
  type ComponentKind,
  type ConfigurationGrant,
  type DescriptorField,
  type FieldValue,
  type FieldValues,
  type ResourceKey,
  valueOf,
} from './descriptor';
import { hiddenFieldIds } from './conditions';
import { evaluate } from './expression';
import type { SingleFieldRole } from './roles';
import {
  at,
  quotaInputsOrDefaults,
  readConfiguration,
  valuesBlockPaths,
} from './values';

export interface RangeBound {
  min: number;
  max: number;
  /** The value is a multiple of it (the schema's `multipleOf`). */
  step: number;
}

/**
 * The bounds of one tier, as the « Configuration » tab reads them: `ranges`,
 * every configurable field bounded by a range, by id; `values`, every
 * configurable field bounded by a list of values, by id (genericity plan,
 * step 6: formerly Odoo's `workers` alone).
 */
export interface ConfigurationBounds {
  values: Record<string, FieldValue[]>;
  ranges: Record<string, RangeBound>;
}

export function configurationBounds(
  descriptor: AppTypeDescriptor,
  grant: ConfigurationGrant,
): ConfigurationBounds {
  const ranges: Record<string, RangeBound> = {};
  const values: Record<string, FieldValue[]> = {};
  for (const field of descriptor.configurableFields) {
    const bound = field.bounds[grant];
    if (bound?.kind === 'range') {
      ranges[field.id] = { min: bound.min, max: bound.max, step: bound.step };
    } else if (bound?.kind === 'values') {
      values[field.id] = [...bound.values];
    }
  }
  return { values, ranges };
}

/** A request and a limit, as written (`100m`, `2Gi`), either side unread. */
export interface QuantityPair {
  request?: string;
  limit?: string;
}

/** What `values` says of one component (`EnvironmentConfigSummary`). */
export interface ComponentConfigSummary {
  component: string;
  /** The descriptor's label of the component. */
  label: string;
  kind: ComponentKind;
  /** The descriptor's `mainComponent`. */
  main: boolean;
  /** Its `enabled` (role `enabled`, else `<valuesPath>.enabled`). */
  enabled?: boolean;
  cpu?: QuantityPair;
  memory?: QuantityPair;
  /**
   * Its pods: an app's `replicas` (role, else `replicaCount`), the
   * CloudNativePG cluster's `instances`.
   */
  pods?: number;
  /** An app's HPA (roles `hpaEnabled`, `hpaMin`, `hpaMax`, else the contract). */
  autoscaling?: { enabled: boolean; min?: number; max?: number };
}

/** One volume's size, as written (`10Gi`). */
export interface VolumeConfigSummary {
  /** The field writing it; absent when read at the contract's path alone. */
  field?: string;
  component: string;
  /** Its component is the descriptor's `mainComponent`. */
  main: boolean;
  /** The field's label, else the component's. */
  label: string;
  /** `volume` (a PVC of the component), `databaseSize` (a database's data). */
  role: 'volume' | 'databaseSize';
  size: string;
}

/** A configurable field without a role (« Autres réglages »). */
export interface SettingConfigSummary {
  field: string;
  label: string;
  value: string | number | boolean;
}

/**
 * What a promotion does not change on its target, generic over the type
 * (genericity plan, step 6): the components in the descriptor's order, the
 * volumes, the other settings — each fact read, nothing guessed.
 */
export interface EnvironmentConfigSummary {
  capacity: ComponentConfigSummary[];
  volumes: VolumeConfigSummary[];
  settings: SettingConfigSummary[];
  /**
   * The configurable fields whose `when` is false for the environment
   * (`hiddenFieldIds`: Odoo's filestore size with its attachments on S3),
   * left out of `volumes` and `settings`; absent when none is.
   */
  hiddenFields?: string[];
}

/** A quantity as written (`100m`, `1Gi`, `2`), or undefined. */
function quantity(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return typeof value === 'string' && /^[0-9.]+[A-Za-z]*$/.test(value)
    ? value
    : undefined;
}

function count(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
    ? value
    : undefined;
}

/** Drops the undefined fields, so the JSON says only what was read. */
function defined<T extends object>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, field]) => field !== undefined),
  ) as T;
}

/** A setting's value when it has the field's type, else undefined. */
function settingValue(
  field: DescriptorField,
  value: unknown,
): string | number | boolean | undefined {
  switch (field.type) {
    case 'boolean':
      return typeof value === 'boolean' ? value : undefined;
    case 'integer':
      return typeof value === 'number' && Number.isInteger(value)
        ? value
        : undefined;
    case 'quantity':
      return quantity(value);
    case 'enum':
      if (field.numericEnum) {
        return typeof value === 'number' && Number.isFinite(value)
          ? value
          : undefined;
      }
      return typeof value === 'string' ? value : undefined;
    default:
      // `databaseMode`, `databaseSync`: their path is the whole CloudNativePG
      // block (`instances`, synchronous replicas…), not one value to show.
      return undefined;
  }
}

const RESOURCE_PATHS: Readonly<Record<ResourceKey, string[]>> = {
  cpuRequest: ['resources', 'requests', 'cpu'],
  cpuLimit: ['resources', 'limits', 'cpu'],
  memoryRequest: ['resources', 'requests', 'memory'],
  memoryLimit: ['resources', 'limits', 'memory'],
};

/**
 * What a promotion does NOT change on its target: the configuration of the
 * environment as written in `values` (a `<env>/values.yaml` of the appenv),
 * for the promotion dialog's « Ce qui ne change pas ». Every component in
 * the descriptor's order, each fact at the path of the field holding its
 * role (`componentRoles`), else at the chart's values contract (platform-
 * catalog `contracts/chart-values.schema.json`) after the component's
 * `valuesPath`: `resources`, `enabled`, `replicaCount` and `autoscaling`
 * (app), `instances` and `storage.size` (cnpg, mariadb for the size),
 * `persistence.size` (the main component, without a volume field); then
 * the `unclassifiedFields`. A value it cannot read, or not of its field's
 * type, is left out — the dialog then says less, never something wrong;
 * undefined when nothing is read. A Valkey block under its other name is
 * read where it is (`valuesBlockPaths`).
 */
export function summarizeEnvironmentConfig(
  descriptor: AppTypeDescriptor,
  values: unknown,
): EnvironmentConfigSummary | undefined {
  const { locate } = valuesBlockPaths(descriptor, values);
  const valueAt = (path: readonly string[]) => at(values, locate(path));
  // Over every field value read, a missing one at its default (a `when`
  // that cannot be evaluated shows its field, conditions.ts).
  const hidden = hiddenFieldIds(descriptor, {
    ...quotaInputsOrDefaults(descriptor, values).values,
    ...readConfiguration(descriptor, values).configuration,
  });
  const capacity: ComponentConfigSummary[] = [];
  const volumes: VolumeConfigSummary[] = [];
  for (const component of descriptor.components) {
    const roles = descriptor.componentRoles.get(component.id);
    const read = (role: SingleFieldRole, contract: string[]): unknown => {
      const id = roles?.roles[role];
      const field = id === undefined ? undefined : descriptor.fieldById.get(id);
      return valueAt(
        field ? field.path : [...component.valuesPath, ...contract],
      );
    };
    const pair = (request: ResourceKey, limit: ResourceKey) => {
      const pairOf = {
        request: quantity(read(request, RESOURCE_PATHS[request])),
        limit: quantity(read(limit, RESOURCE_PATHS[limit])),
      };
      return pairOf.request || pairOf.limit ? defined(pairOf) : undefined;
    };
    const enabled = read('enabled', ['enabled']);
    const app = component.kind === 'app';
    const hpaEnabled = app
      ? read('hpaEnabled', ['autoscaling', 'enabled'])
      : undefined;
    let pods: number | undefined;
    if (app) {
      pods = count(read('replicas', ['replicaCount']));
    } else if (component.kind === 'cnpg') {
      pods = count(valueAt([...component.valuesPath, 'instances']));
    }
    const summary = defined<ComponentConfigSummary>({
      component: component.id,
      label: component.label,
      kind: component.kind,
      main: component === descriptor.mainComponent,
      enabled: typeof enabled === 'boolean' ? enabled : undefined,
      cpu: pair('cpuRequest', 'cpuLimit'),
      memory: pair('memoryRequest', 'memoryLimit'),
      pods,
      autoscaling:
        typeof hpaEnabled === 'boolean'
          ? defined({
              enabled: hpaEnabled,
              min: count(read('hpaMin', ['autoscaling', 'minReplicas'])),
              max: count(read('hpaMax', ['autoscaling', 'maxReplicas'])),
            })
          : undefined,
    });
    if (
      summary.enabled !== undefined ||
      summary.cpu ||
      summary.memory ||
      summary.pods !== undefined ||
      summary.autoscaling
    ) {
      capacity.push(summary);
    }

    const volumeFields = roles?.volumes ?? [];
    for (const id of volumeFields) {
      if (hidden.has(id)) continue;
      const field = descriptor.fieldById.get(id);
      const size = field ? quantity(valueAt(field.path)) : undefined;
      if (field && size) {
        volumes.push({
          field: id,
          component: component.id,
          main: summary.main,
          label: field.label,
          role: 'volume',
          size,
        });
      }
    }
    if (volumeFields.length === 0 && summary.main) {
      const size = quantity(
        valueAt([...component.valuesPath, 'persistence', 'size']),
      );
      if (size) {
        volumes.push({
          component: component.id,
          main: true,
          label: component.label,
          role: 'volume',
          size,
        });
      }
    }
    if (component.kind === 'cnpg' || component.kind === 'mariadb') {
      const id = roles?.roles.databaseSize;
      const field = id === undefined ? undefined : descriptor.fieldById.get(id);
      const size = quantity(
        valueAt(
          field ? field.path : [...component.valuesPath, 'storage', 'size'],
        ),
      );
      if (size) {
        volumes.push({
          ...(field ? { field: field.id } : {}),
          component: component.id,
          main: summary.main,
          label: field ? field.label : component.label,
          role: 'databaseSize',
          size,
        });
      }
    }
  }
  const settings: SettingConfigSummary[] = [];
  for (const id of descriptor.unclassifiedFields) {
    if (hidden.has(id)) continue;
    const field = descriptor.fieldById.get(id);
    const value = field ? settingValue(field, valueAt(field.path)) : undefined;
    if (field && value !== undefined) {
      settings.push({ field: id, label: field.label, value });
    }
  }
  if (capacity.length + volumes.length + settings.length === 0) {
    return undefined;
  }
  return hidden.size > 0
    ? { capacity, volumes, settings, hiddenFields: [...hidden] }
    : { capacity, volumes, settings };
}

/** What CloudNativePG must run once a configuration is applied. */
export interface DatabaseFigures {
  instances: number;
  /** Millicores and Mi, of each instance's `postgres` container. */
  cpuRequest: number;
  cpuLimit: number;
  memoryRequest: number;
  memoryLimit: number;
  /** Gi, of each instance's data volume; absent when no field sets it. */
  storage?: number;
}

/** The field writing the CloudNativePG block's `storage.size`, if any. */
export function databaseStorageField(
  descriptor: AppTypeDescriptor,
): DescriptorField | undefined {
  const cnpg = descriptor.databaseComponent;
  const path = cnpg ? [...cnpg.valuesPath, 'storage', 'size'].join('.') : '';
  return descriptor.configurableFields.find(
    field => field.path.join('.') === path,
  );
}

/**
 * The fields of the main component's files volumes — the application's
 * files: its fields of role `volume` (`componentRoles`), else the field
 * writing its `persistence.size` (the values contract), if any.
 */
export function filesStorageFields(
  descriptor: AppTypeDescriptor,
): DescriptorField[] {
  const main = descriptor.mainComponent;
  if (!main) {
    return [];
  }
  const byRole = (descriptor.componentRoles.get(main.id)?.volumes ?? [])
    .map(id => descriptor.fieldById.get(id))
    .filter((field): field is DescriptorField => field !== undefined);
  if (byRole.length > 0) {
    return byRole;
  }
  const path = [...main.valuesPath, 'persistence', 'size'].join('.');
  return descriptor.configurableFields.filter(
    field => field.path.join('.') === path,
  );
}

/** The fields of the CloudNativePG component: their change is reconciled. */
export function databaseFieldIds(descriptor: AppTypeDescriptor): Set<string> {
  const cnpg = descriptor.databaseComponent;
  return new Set(
    cnpg
      ? descriptor.configurableFields
          .filter(field => field.component === cnpg.id)
          .map(field => field.id)
      : [],
  );
}

/**
 * The CloudNativePG figures of `configuration`: the instances of its mode
 * (else the component's `pods`), the component's resources, the data
 * volume's size. Undefined for a type without CloudNativePG, or whose
 * figures are not all numbers.
 */
export function databaseFiguresOf(
  descriptor: AppTypeDescriptor,
  configuration: FieldValues,
): DatabaseFigures | undefined {
  const cnpg = descriptor.databaseComponent;
  if (!cnpg) {
    return undefined;
  }
  const modeField = descriptor.configurableFields.find(
    field => field.type === 'databaseMode',
  );
  const mode = modeField ? valueOf(configuration, modeField.id) : undefined;
  const instances = isDatabaseMode(mode)
    ? MODE_INSTANCES[mode]
    : evaluate(cnpg.pods, { values: configuration });
  const figure = (
    key: 'cpuRequest' | 'cpuLimit' | 'memoryRequest' | 'memoryLimit',
  ) => {
    const ref = cnpg.resources[key];
    const value = typeof ref === 'string' ? valueOf(configuration, ref) : ref;
    return typeof value === 'number' ? value : undefined;
  };
  const cpuRequest = figure('cpuRequest');
  const cpuLimit = figure('cpuLimit');
  const memoryRequest = figure('memoryRequest');
  const memoryLimit = figure('memoryLimit');
  if (
    typeof instances !== 'number' ||
    cpuRequest === undefined ||
    cpuLimit === undefined ||
    memoryRequest === undefined ||
    memoryLimit === undefined
  ) {
    return undefined;
  }
  const storageField = databaseStorageField(descriptor);
  const storage = storageField
    ? valueOf(configuration, storageField.id)
    : undefined;
  return {
    instances,
    cpuRequest,
    cpuLimit,
    memoryRequest,
    memoryLimit,
    ...(typeof storage === 'number' ? { storage } : {}),
  };
}
